import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { input, btnPrimary, btnGhost, fmtDate } from "./ui";
import { FileText, History, RotateCcw } from "lucide-react";

export default function DocTemplateDialog({ open, onOpenChange }) {
  const [tpl, setTpl] = useState(null);
  const [versions, setVersions] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/attendance-template");
      setTpl(data);
      const { data: v } = await api.get("/settings/attendance-template/versions");
      setVersions(v);
    } catch { toast.error("Não consegui carregar o texto-base."); }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const setBody = (i, v) => {
    const sections = tpl.sections.map((s, j) => (j === i ? { ...s, body: v } : s));
    setTpl({ ...tpl, sections });
  };

  const save = async () => {
    try {
      const { data } = await api.put("/settings/attendance-template", { sections: tpl.sections });
      toast.success(`Texto-base salvo como versão ${data.version}.`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao salvar o texto-base."); }
  };

  const restore = async (v) => {
    try {
      await api.put("/settings/attendance-template", { sections: v.sections });
      toast.success(`Versão ${v.version} restaurada como nova versão.`);
      load();
    } catch { toast.error("Erro ao restaurar."); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FDFBF7] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#2C332A] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#5B6E5B]" /> Texto-base do documento
          </DialogTitle>
          <DialogDescription className="font-body text-sm text-[#7A8A7A]">
            Editável apenas aqui no painel, com controle de versão. A versão definitiva precisa da sua revisão e da revisão jurídica antes do uso.
          </DialogDescription>
        </DialogHeader>
        {!tpl ? <p className="font-body text-sm text-[#7A8A7A]">Carregando...</p> : (
          <div className="space-y-4" data-testid="tpl-dialog">
            <p className="font-body text-xs text-[#B2644D] bg-[#C87A5E]/10 rounded-xl p-3" data-testid="tpl-review-note">
              Versão atual: v{tpl.version} · status: em revisão profissional e jurídica. Campos entre chaves — como {"{valor}"} ou {"{modalidade}"} — são preenchidos automaticamente com os dados de cada paciente.
            </p>
            {tpl.sections.map((s, i) => (
              <div key={s.id || i}>
                <p className="font-body text-xs font-medium text-[#4A5D4E] mb-1.5">{s.title}{s.online_only ? " (só entra no PDF quando a modalidade é online)" : ""}</p>
                <textarea rows={Math.min(8, (s.body || "").split("\n").length + 1)} className={`${input} resize-y text-[13px]`}
                  value={s.body} onChange={(e) => setBody(i, e.target.value)} data-testid={`tpl-body-${s.id || i}`} />
              </div>
            ))}
            <div className="flex justify-end gap-3">
              <button className={btnGhost} onClick={() => onOpenChange(false)}>Fechar</button>
              <button className={btnPrimary} onClick={save} data-testid="tpl-save">Salvar nova versão</button>
            </div>
            {versions.length > 0 && (
              <div className="rounded-xl border border-[#5B6E5B]/15 p-4">
                <p className="font-body text-xs uppercase tracking-wider text-[#5B6E5B] mb-2 flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Versões anteriores</p>
                <div className="divide-y divide-[#5B6E5B]/10">
                  {versions.map((v) => (
                    <div key={v.id} className="flex items-center justify-between py-2" data-testid={`tpl-version-${v.version}`}>
                      <p className="font-body text-sm text-[#2C332A]">v{v.version} · {fmtDate((v.saved_at || "").slice(0, 10))} · {v.saved_by}</p>
                      <button className="inline-flex items-center gap-1 text-xs text-[#5B6E5B] hover:text-[#2C332A]" onClick={() => restore(v)} data-testid={`tpl-restore-${v.version}`}>
                        <RotateCcw className="w-3.5 h-3.5" /> Restaurar
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
